import { useMemo, useState } from "react";

import { t } from "../../../i18n";
import { cn } from "../../../lib/cn";
import { formatNumericDate } from "../../../lib/format";
import type { ExpenseLayout } from "../../../lib/settings";
import {
  commonMeaningfulTokens,
  findExactMatchSiblings,
  findSimilarSiblings,
  tokenize,
} from "../../../lib/similarity";
import { trpc } from "../../../lib/trpc";
import {
  Avatar,
  Badge,
  DeletingOverlay,
  DropdownMenu,
  Icon,
  IconButton,
  MenuItem,
  Money,
  Switch,
  Table,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  useToast,
} from "../../ui";
import { CategoryPicker } from "../components/CategoryPicker";
import { CopyableDescription } from "../components/CopyableDescription";
import { SortableHeader } from "../components/SortableHeader";
import { AdjustSplitDialog } from "../dialogs/AdjustSplitDialog";
import { DeleteExpenseDialog } from "../dialogs/DeleteExpenseDialog";
import { EditExpenseDialog } from "../dialogs/EditExpenseDialog";
import { SmartCategorizeDialogs } from "../dialogs/SmartCategorizeDialogs";
import { useCategoryMaps } from "../hooks/useCategoryMaps";
import type {
  Expense,
  Member,
  Settlement,
  SettlementCategory,
  SmartSuggestion,
  SortDirection,
  SortKey,
} from "../types";
import { describeSplit, hasCustomSplit } from "../utils";

interface ExpensesTableProps {
  settlementId: string;
  householdId: string;
  /** The full expenses list for the settlement, used for smart-categorisation
   *  detection so suggestions can reference rows hidden by the active filter. */
  allExpenses: Expense[];
  expenses: Expense[];
  invoices: Settlement["invoices"];
  members: Member[];
  categories: SettlementCategory[];
  currency: string;
  isLocked: boolean;
  layout: ExpenseLayout;
  search: string;
}

export function ExpensesTable({
  settlementId,
  householdId,
  allExpenses,
  expenses,
  invoices,
  members,
  categories,
  currency,
  isLocked,
  layout,
  search,
}: ExpensesTableProps) {
  const utils = trpc.useUtils();
  // Optimistic update — toggling "fellesutgift" or swapping a category used
  // to wait for the server roundtrip and a full settlement refetch (which
  // re-runs DEK-decryption on every invoice filename). With optimistic
  // patching the UI flips instantly; if the mutation actually fails we roll
  // back to the previous cached state.
  const updateExpense = trpc.settlement.updateExpense.useMutation({
    onMutate: async (variables) => {
      await utils.settlement.get.cancel({ settlementId });
      const previous = utils.settlement.get.getData({ settlementId });
      // Cast through `unknown` because settlement.get's inferred type is a
      // union of mock + real-API shapes; at runtime both branches accept
      // these patched fields. The cast keeps the optimistic-update path
      // out of the TS divergence between the two shapes.
      utils.settlement.get.setData({ settlementId }, (old: unknown) => {
        if (!old) return old as never;
        const o = old as { expenses: Expense[] } & Record<string, unknown>;
        return {
          ...o,
          expenses: o.expenses.map((e) => {
            if (e.id !== variables.expenseId) return e;
            return {
              ...e,
              isShared: variables.isShared !== undefined ? variables.isShared : e.isShared,
              categoryId: variables.categoryId !== undefined ? variables.categoryId : e.categoryId,
              description:
                variables.description !== undefined ? variables.description : e.description,
              amount: variables.amount !== undefined ? variables.amount : e.amount,
              date: variables.date !== undefined ? variables.date : e.date,
              paidByUserId:
                variables.paidByUserId !== undefined ? variables.paidByUserId : e.paidByUserId,
            };
          }),
        } as never;
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        utils.settlement.get.setData({ settlementId }, context.previous);
      }
    },
    onSettled: () => utils.settlement.get.invalidate({ settlementId }),
  });

  const [editing, setEditing] = useState<Expense | null>(null);
  const [adjusting, setAdjusting] = useState<Expense | null>(null);
  const [deleting, setDeleting] = useState<Expense | null>(null);
  const [pendingDeletions, setPendingDeletions] = useState<Set<string>>(() => new Set());
  const [suggestion, setSuggestion] = useState<SmartSuggestion | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({
    key: "date",
    direction: "asc",
  });

  const { show: showToast } = useToast();
  const removeExpense = trpc.settlement.deleteExpense.useMutation({
    onMutate: ({ expenseId }) => {
      setPendingDeletions((prev) => {
        const next = new Set(prev);
        next.add(expenseId);
        return next;
      });
    },
    onSuccess: async (_, { expenseId }) => {
      await utils.settlement.get.invalidate({ settlementId });
      setPendingDeletions((prev) => {
        if (!prev.has(expenseId)) return prev;
        const next = new Set(prev);
        next.delete(expenseId);
        return next;
      });
    },
    onError: (err, { expenseId }) => {
      setPendingDeletions((prev) => {
        if (!prev.has(expenseId)) return prev;
        const next = new Set(prev);
        next.delete(expenseId);
        return next;
      });
      showToast({
        tone: "danger",
        title: t.settlement.expenses.deleteFailedToast,
        description: err.message,
        dismissLabel: t.common.close,
      });
    },
  });

  const confirmDelete = (expense: Expense) => {
    removeExpense.mutate({ expenseId: expense.id });
    setDeleting(null);
  };

  // Stable per-render lookup Maps. Shared with other places that need
  // member/category lookup via `useCategoryMaps` — see hook for the why.
  const { memberMap, categoryMap } = useCategoryMaps(members, categories);

  /**
   * Wraps the per-row category change. Fires the mutation first (so the
   * optimistic update lands immediately) then runs the smart-categorisation
   * heuristics against the unfiltered settlement to propose either a
   * bulk-update or a pattern-save dialog. Same handler is used by both the
   * card layout and the table layout.
   */
  const onCategoryChange = (
    expense: Expense,
    prevCategoryId: string | null,
    targetCategoryId: string | null,
  ) => {
    updateExpense.mutate({ expenseId: expense.id, categoryId: targetCategoryId });
    if (!targetCategoryId || targetCategoryId === prevCategoryId) return;
    const others = allExpenses.filter((x) => x.id !== expense.id);
    const exact = findExactMatchSiblings(expense, others, targetCategoryId);
    if (exact.length > 0) {
      setSuggestion({
        kind: "bulk",
        expenseId: expense.id,
        targetCategoryId,
        description: expense.description,
        matches: exact,
      });
      return;
    }
    const targetCat = categoryMap.get(targetCategoryId);
    // Seed categories (householdId === null) cannot be edited in place —
    // updateCategory would silently fork them, which is too surprising
    // from inside a settlement view. Skip the pattern suggestion entirely;
    // the user can add it from the household-settings screen.
    if (!targetCat || targetCat.householdId == null) return;
    const similar = findSimilarSiblings(expense, others, targetCategoryId);
    if (similar.length === 0) return;
    const targetTokens = new Set(tokenize(expense.description));
    const tokens = commonMeaningfulTokens(
      targetTokens,
      ...similar.map((s) => new Set(tokenize(s.expense.description))),
    );
    if (tokens.length === 0) return;
    setSuggestion({
      kind: "pattern",
      expenseId: expense.id,
      targetCategoryId,
      commonTokens: tokens,
    });
  };
  const invoiceMap = new Map(invoices.map((i) => [i.id, i]));

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );

  const sortedExpenses = useMemo(() => {
    const payerName = (e: Expense) => {
      const m = memberMap.get(e.paidByUserId);
      return (m?.displayName ?? m?.email ?? "").toLocaleLowerCase("nb-NO");
    };
    const categoryName = (e: Expense) =>
      (e.categoryId ? (categoryMap.get(e.categoryId)?.name ?? "") : "").toLocaleLowerCase("nb-NO");

    const compare = (a: Expense, b: Expense): number => {
      switch (sort.key) {
        case "date":
          return a.date.localeCompare(b.date);
        case "description":
          return a.description.localeCompare(b.description, "nb-NO");
        case "amount":
          return Number.parseFloat(a.amount) - Number.parseFloat(b.amount);
        case "category":
          return categoryName(a).localeCompare(categoryName(b), "nb-NO");
        case "paidBy":
          return payerName(a).localeCompare(payerName(b), "nb-NO");
      }
    };

    return [...expenses].sort((a, b) => {
      const primary = compare(a, b);
      if (primary !== 0) return sort.direction === "asc" ? primary : -primary;
      return a.date.localeCompare(b.date);
    });
  }, [expenses, sort, memberMap, categoryMap]);

  const renderBadges = (e: Expense) => {
    const manual = e.source === "manual";
    const edited = e.manuallyEdited;
    const customSplit = hasCustomSplit(e, members);
    if (!manual && !edited && !customSplit) return null;
    return (
      <div className="flex flex-wrap items-center gap-1">
        {manual && (
          <Badge tone="info" size="sm">
            {t.settlement.expenses.table.manualBadge}
          </Badge>
        )}
        {edited && (
          <Badge tone="neutral" size="sm" iconLeft="pencil">
            {t.settlement.expenses.table.editedBadge}
          </Badge>
        )}
        {customSplit && (
          <Badge
            tone="accent"
            size="sm"
            iconLeft="arrow-right-left"
            title={describeSplit(e.splits, memberMap, e.currency || currency)}
          >
            {t.settlement.expenses.table.customSplitBadge}
          </Badge>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Card-layout — full info uten kolonneskjuling, og samme
          optimistic-handlere som tabellen for snappy interaksjon. */}
      <ul className={cn("space-y-2", layout === "card" ? "sm:hidden" : "hidden")}>
        {sortedExpenses.map((e, index) => {
          const cat = e.categoryId ? categoryMap.get(e.categoryId) : null;
          const payer = memberMap.get(e.paidByUserId);
          const invoice = e.invoiceId ? invoiceMap.get(e.invoiceId) : null;
          return (
            <li
              key={e.id}
              className="relative flex flex-col gap-3.5 overflow-hidden rounded-md border border-ink-100 bg-ink-50/60 p-4 dark:border-ink-200 dark:bg-ink-100/30"
            >
              {pendingDeletions.has(e.id) && (
                <DeletingOverlay label={t.settlement.expenses.deletingOverlay} />
              )}
              {/* Tittel + actions topp-hoyre. Dato over beskrivelse.
                  Info-knapp viser metadata (kilde, faktura, kortholder, fordeling)
                  som popover — synlig kun hvis det finnes informasjon som ikke
                  er apenbart i selve raden. */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-ink-500 text-xs tabular-nums">
                    {String(index + 1).padStart(2, "0")} · {formatNumericDate(e.date)}
                  </span>
                  <div className="flex flex-col items-start gap-1">
                    <CopyableDescription description={e.description} search={search} />
                    {e.source === "live" && (e.cardAspspName || e.cardLastFour) && (
                      <CardSourceChip aspspName={e.cardAspspName} lastFour={e.cardLastFour} />
                    )}
                    {e.source === "invoice" && invoice && (
                      <InvoiceSourceChip filename={invoice.filename} />
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {(invoice ||
                    e.payerUnresolved ||
                    hasCustomSplit(e, members) ||
                    e.source === "manual" ||
                    e.source === "live") && (
                    <DropdownMenu
                      trigger={
                        <IconButton
                          icon="info"
                          label={t.settlement.expenses.cardDetails.infoLabel}
                          size="sm"
                        />
                      }
                    >
                      <dl className="max-w-[18rem] space-y-1.5 px-3 py-2 text-xs">
                        <div className="flex gap-2">
                          <dt className="shrink-0 text-ink-500">
                            {t.settlement.expenses.cardDetails.sourceLabel}
                          </dt>
                          <dd className="break-words text-ink-800">
                            {e.source === "manual"
                              ? t.settlement.expenses.cardDetails.sourceManual
                              : e.source === "live"
                                ? t.settlement.expenses.cardDetails.sourceLive
                                : t.settlement.expenses.cardDetails.sourceInvoice}
                          </dd>
                        </div>
                        {invoice && (
                          <div className="flex gap-2">
                            <dt className="shrink-0 text-ink-500">
                              {t.settlement.expenses.cardDetails.invoiceLabel}
                            </dt>
                            <dd className="break-words text-ink-800">{invoice.filename}</dd>
                          </div>
                        )}
                        {e.payerUnresolved && (
                          <div className="flex gap-2">
                            <dt className="shrink-0 text-ink-500">
                              {t.settlement.expenses.cardDetails.cardholderLabel}
                            </dt>
                            <dd className="break-words text-ink-800">
                              {e.cardholderName ??
                                t.settlement.expenses.cardDetails.cardholderUnknown}
                            </dd>
                          </div>
                        )}
                        {hasCustomSplit(e, members) && (
                          <div className="flex gap-2">
                            <dt className="shrink-0 text-ink-500">
                              {t.settlement.expenses.cardDetails.splitLabel}
                            </dt>
                            <dd className="break-words text-ink-800">
                              {describeSplit(e.splits, memberMap, e.currency || currency)}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </DropdownMenu>
                  )}
                  {!isLocked && (
                    <DropdownMenu
                      trigger={<IconButton icon="more-vertical" label={t.common.more} size="sm" />}
                    >
                      <MenuItem icon="edit" onClick={() => setEditing(e)}>
                        {t.settlement.expenses.table.rowActions.edit}
                      </MenuItem>
                      <MenuItem icon="arrow-right-left" onClick={() => setAdjusting(e)}>
                        {t.settlement.expenses.table.rowActions.adjustSplit}
                      </MenuItem>
                      <MenuItem icon="trash" destructive onClick={() => setDeleting(e)}>
                        {t.settlement.expenses.table.rowActions.delete}
                      </MenuItem>
                    </DropdownMenu>
                  )}
                </div>
              </div>

              {/* Kategori + felles-toggle. Label vises for switch via flex-row-reverse. */}
              <div className="flex items-center justify-between gap-3">
                <CategoryPicker
                  categories={categories}
                  selected={cat ?? null}
                  disabled={isLocked}
                  onChange={(categoryId) => onCategoryChange(e, e.categoryId, categoryId)}
                />
                <Switch
                  checked={e.isShared}
                  onCheckedChange={(checked) =>
                    updateExpense.mutate({ expenseId: e.id, isShared: checked })
                  }
                  disabled={isLocked}
                  label={t.settlement.expenses.table.type}
                  className="flex-row-reverse [&_span]:text-xs"
                />
              </div>

              {renderBadges(e)}

              {/* Separator + betalt av + belop i bunnen */}
              <hr className="mt-1 border-0 border-ink-100 border-t dark:border-ink-200/60" />
              <div className="flex items-center justify-between gap-3">
                {payer ? (
                  <div className="flex min-w-0 items-center gap-1.5">
                    <Avatar name={payer.displayName ?? payer.email} size="sm" />
                    <span className="truncate text-ink-700 text-xs">
                      {payer.displayName ?? payer.email.split("@")[0]}
                    </span>
                  </div>
                ) : (
                  <span />
                )}
                <span className="shrink-0 font-semibold text-base text-ink-900 tabular-nums">
                  <Money amount={e.amount} currency={e.currency || currency} />
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <div className={cn(layout === "card" ? "hidden sm:block" : "block")}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell className="w-12 text-ink-500">
                {t.settlement.expenses.table.rowNumber}
              </TableHeaderCell>
              <SortableHeader
                label={t.settlement.expenses.table.date}
                columnKey="date"
                activeKey={sort.key}
                direction={sort.direction}
                onToggle={toggleSort}
              />
              <SortableHeader
                label={t.settlement.expenses.table.description}
                columnKey="description"
                activeKey={sort.key}
                direction={sort.direction}
                onToggle={toggleSort}
              />
              <SortableHeader
                label={t.settlement.expenses.table.category}
                columnKey="category"
                activeKey={sort.key}
                direction={sort.direction}
                onToggle={toggleSort}
              />
              <SortableHeader
                label={t.settlement.expenses.table.paidBy}
                columnKey="paidBy"
                activeKey={sort.key}
                direction={sort.direction}
                onToggle={toggleSort}
              />
              <TableHeaderCell>{t.settlement.expenses.table.type}</TableHeaderCell>
              <SortableHeader
                label={t.settlement.expenses.table.amount}
                columnKey="amount"
                activeKey={sort.key}
                direction={sort.direction}
                onToggle={toggleSort}
                align="right"
              />
              <TableHeaderCell />
            </TableRow>
          </TableHead>
          {sortedExpenses.map((e, index) => {
            const cat = e.categoryId ? categoryMap.get(e.categoryId) : null;
            const payer = memberMap.get(e.paidByUserId);
            const invoice = e.invoiceId ? invoiceMap.get(e.invoiceId) : null;
            const badges = renderBadges(e);
            // One <tbody> per expense isolates hover and zebra to the
            // expense's own rows — peer/peer-hover selectors only match
            // siblings of the same parent, so they can't bleed into the
            // next expense's badge row.
            //
            // Hover and zebra tones: dark-mode `ink-50` is near-black, so
            // we use explicit `dark:` variants on a lighter token (ink-300)
            // to keep the lift visible against the surface.
            const zebra = index % 2 === 1 ? "bg-ink-50/50 dark:bg-ink-200/30" : undefined;
            const hoverBg = "hover:bg-ink-100 dark:hover:bg-ink-300/40";
            const peerHoverBg = "peer-hover:bg-ink-100 dark:peer-hover:bg-ink-300/40";
            const hasSiblingHoverBg =
              "has-[+tr:hover]:bg-ink-100 dark:has-[+tr:hover]:bg-ink-300/40";
            const topBorder = index > 0 ? "[&>td]:border-t [&>td]:border-ink-100" : undefined;
            if (pendingDeletions.has(e.id)) {
              return (
                <tbody key={e.id}>
                  <tr
                    aria-busy="true"
                    className={cn("animate-fade-in bg-primary/10 dark:bg-primary/20", topBorder)}
                  >
                    <td colSpan={8} className="px-3 py-3 text-center">
                      <output className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1.5 font-medium text-primary-fg text-xs">
                        <Icon name="loader" size="xs" className="animate-spin" />
                        <span>{t.settlement.expenses.deletingOverlay}</span>
                      </output>
                    </td>
                  </tr>
                </tbody>
              );
            }
            return (
              <tbody key={e.id}>
                <tr
                  className={cn(
                    "transition-colors",
                    zebra,
                    hoverBg,
                    badges && cn("peer [&>td]:!pb-1.5", hasSiblingHoverBg),
                    topBorder,
                  )}
                >
                  <TableCell className="text-ink-500 tabular-nums">{index + 1}</TableCell>
                  <TableCell className="whitespace-nowrap text-ink-500 tabular-nums">
                    {formatNumericDate(e.date)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      <CopyableDescription description={e.description} search={search} />
                      {e.source === "live" && (e.cardAspspName || e.cardLastFour) && (
                        <CardSourceChip aspspName={e.cardAspspName} lastFour={e.cardLastFour} />
                      )}
                      {e.source === "invoice" && invoice && (
                        <InvoiceSourceChip filename={invoice.filename} />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <CategoryPicker
                      categories={categories}
                      selected={cat ?? null}
                      disabled={isLocked}
                      onChange={(categoryId) => onCategoryChange(e, e.categoryId, categoryId)}
                    />
                  </TableCell>
                  <TableCell>
                    {payer && (
                      <div className="flex items-center gap-1.5">
                        <Avatar name={payer.displayName ?? payer.email} size="sm" />
                        <span className="text-ink-700 text-xs">
                          {payer.displayName ?? payer.email.split("@")[0]}
                        </span>
                        {e.payerUnresolved && (
                          <Badge
                            tone="warning"
                            size="sm"
                            iconLeft="alert-circle"
                            title={t.settlement.expenses.table.payerUnresolvedTooltip(
                              e.cardholderName,
                            )}
                          >
                            {t.settlement.expenses.table.payerUnresolvedBadge}
                          </Badge>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={e.isShared}
                      onCheckedChange={(checked) =>
                        updateExpense.mutate({ expenseId: e.id, isShared: checked })
                      }
                      disabled={isLocked}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Money amount={e.amount} currency={e.currency || currency} />
                  </TableCell>
                  <TableCell align="right">
                    {!isLocked && (
                      <DropdownMenu
                        trigger={
                          <IconButton icon="more-vertical" label={t.common.more} size="sm" />
                        }
                      >
                        <MenuItem icon="edit" onClick={() => setEditing(e)}>
                          {t.settlement.expenses.table.rowActions.edit}
                        </MenuItem>
                        <MenuItem icon="arrow-right-left" onClick={() => setAdjusting(e)}>
                          {t.settlement.expenses.table.rowActions.adjustSplit}
                        </MenuItem>
                        <MenuItem icon="trash" destructive onClick={() => setDeleting(e)}>
                          {t.settlement.expenses.table.rowActions.delete}
                        </MenuItem>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </tr>
                {badges && (
                  <tr className={cn("transition-colors", zebra, hoverBg, peerHoverBg)}>
                    <td colSpan={8} className="!pt-0 px-3 pb-2.5">
                      {badges}
                    </td>
                  </tr>
                )}
              </tbody>
            );
          })}
        </Table>
      </div>

      {editing && (
        <EditExpenseDialog
          key={editing.id}
          settlementId={settlementId}
          expense={editing}
          members={members}
          categories={categories}
          onClose={() => setEditing(null)}
        />
      )}
      {adjusting && (
        <AdjustSplitDialog
          key={adjusting.id}
          settlementId={settlementId}
          expense={adjusting}
          members={members}
          currency={currency}
          onClose={() => setAdjusting(null)}
        />
      )}
      {deleting && (
        <DeleteExpenseDialog
          key={deleting.id}
          expense={deleting}
          onClose={() => setDeleting(null)}
          onConfirm={confirmDelete}
        />
      )}
      <SmartCategorizeDialogs
        suggestion={suggestion}
        allExpenses={allExpenses}
        categories={categories}
        householdId={householdId}
        currency={currency}
        settlementId={settlementId}
        onClose={() => setSuggestion(null)}
      />
    </>
  );
}

/**
 * Small chip rendered next to live-sourced expense descriptions, showing the
 * card it came from — e.g. "SAS EuroBonus ··8841". Falls back gracefully if
 * either field is missing (disconnected card with denormalised fields lost).
 */
/**
 * Source chip rendered next to an expense's description. Shared by live and
 * invoice rows so the visual language is consistent: `Fra: [ikon] [label]`.
 *
 * - Live: `Fra: 💳 SAS EuroBonus Mastercard ··8841`
 * - Invoice: `Fra: 📄 faktura-2026-05.pdf`
 */
function SourceChip({ kind, label }: { kind: "live" | "invoice"; label: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-ink-100 px-1.5 py-0.5 font-medium text-ink-700 text-xs dark:bg-ink-200"
      title={label}
    >
      <span className="text-ink-500">{t.settlement.expenses.sourcePrefix}</span>
      <Icon name={kind === "live" ? "credit-card" : "file-text"} size="xs" />
      <span>{label}</span>
    </span>
  );
}

function CardSourceChip({
  aspspName,
  lastFour,
}: {
  aspspName: string | null | undefined;
  lastFour: string | null | undefined;
}) {
  const aspsp = aspspName ?? "";
  const last4 = lastFour ?? null;
  const label =
    !aspsp && !last4
      ? t.settlement.expenses.cardSourceChipUnknown
      : t.settlement.expenses.cardSourceChip(aspsp, last4);
  return <SourceChip kind="live" label={label} />;
}

function InvoiceSourceChip({ filename }: { filename: string }) {
  return <SourceChip kind="invoice" label={t.settlement.expenses.sourceFromInvoice(filename)} />;
}
